// src/pages/AuthPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { websiteSignInDestination } from '../website/applicationRoutes';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Sparkles, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '../api/client';
import { MiraalIcon, MiraalWordmark } from '../components/Brand';

// ═══════════════════════════════════════════════════════════════════════════
// NEURAL MESH BACKGROUND (Canvas) — Your existing beautiful animation
// ═══════════════════════════════════════════════════════════════════════════

const NeuralMeshBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const robotNodes = [
      { x: 0.72, y: 0.15, glow: true }, { x: 0.75, y: 0.12, glow: true },
      { x: 0.78, y: 0.15, glow: true }, { x: 0.76, y: 0.18, glow: true },
      { x: 0.73, y: 0.20, glow: false }, { x: 0.77, y: 0.20, glow: false },
      { x: 0.74, y: 0.23, glow: false }, { x: 0.76, y: 0.23, glow: false },
      { x: 0.70, y: 0.28, glow: true }, { x: 0.80, y: 0.28, glow: true },
      { x: 0.68, y: 0.32, glow: false }, { x: 0.82, y: 0.32, glow: false },
      { x: 0.72, y: 0.38, glow: false }, { x: 0.78, y: 0.38, glow: false },
      { x: 0.71, y: 0.45, glow: false }, { x: 0.79, y: 0.45, glow: false },
      { x: 0.73, y: 0.52, glow: false }, { x: 0.77, y: 0.52, glow: false },
      { x: 0.65, y: 0.30, glow: false }, { x: 0.58, y: 0.38, glow: false },
      { x: 0.50, y: 0.48, glow: false }, { x: 0.42, y: 0.58, glow: true },
      { x: 0.38, y: 0.68, glow: true },
      { x: 0.35, y: 0.72, glow: true }, { x: 0.32, y: 0.75, glow: true },
      { x: 0.30, y: 0.78, glow: true },
    ];

    const connections = [
      [0,1], [1,2], [2,3], [3,0], [0,4], [1,5], [4,5],
      [4,6], [5,7], [6,7], [6,8], [7,9], [8,10], [9,11],
      [8,12], [9,13], [12,14], [13,15], [14,16], [15,17],
      [8,18], [18,19], [19,20], [20,21], [21,22],
      [22,23], [23,24], [24,25], [22,24], [23,25],
    ];

    const sphereCenter = { x: 0.32, y: 0.72 };
    const sphereNodes: any[] = [];
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      sphereNodes.push({ x: sphereCenter.x + Math.cos(angle) * 0.06, y: sphereCenter.y + Math.sin(angle) * 0.06, glow: true });
    }
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      sphereNodes.push({ x: sphereCenter.x + Math.cos(angle) * 0.03, y: sphereCenter.y + Math.sin(angle) * 0.03, glow: true });
    }

    const allNodes = [...robotNodes, ...sphereNodes];
    const sphereStart = robotNodes.length;

    for (let i = 0; i < 16; i++) {
      connections.push([sphereStart + i, sphereStart + ((i + 1) % 16)]);
      connections.push([sphereStart + i, sphereStart + ((i + 4) % 16)]);
    }
    for (let i = 0; i < 8; i++) {
      connections.push([sphereStart + 16 + i, sphereStart + 16 + ((i + 1) % 8)]);
    }
    for (let i = 0; i < 8; i++) {
      connections.push([sphereStart + 16 + i, sphereStart + i * 2]);
    }

    const particles: any[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        color: ['#ffffff', '#cccccc', '#aaaaaa', '#bbbbbb', '#999999'][Math.floor(Math.random() * 5)]
      });
    }

    let animId: number;
    const draw = () => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, w, h);

      const vignette = ctx.createRadialGradient(w/2, h/2, w*0.3, w/2, h/2, w*0.8);
      vignette.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
      vignette.addColorStop(1, 'rgba(255, 255, 255, 0.05)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, w, h);

      const time = Date.now() / 1000;
      const scale = Math.min(w, h) * 1.1;
      const offsetX = w * 0.05;
      const offsetY = h * 0.05;

      const glowGradient = ctx.createRadialGradient(w * 0.55, h * 0.45, 0, w * 0.55, h * 0.45, scale * 0.5);
      glowGradient.addColorStop(0, 'rgba(255, 255, 255, 0.15)');
      glowGradient.addColorStop(0.3, 'rgba(200, 200, 200, 0.1)');
      glowGradient.addColorStop(1, 'transparent');
      ctx.fillStyle = glowGradient;
      ctx.fillRect(w * 0.2, 0, w * 0.8, h);

      connections.forEach(([i, j]) => {
        if (i >= allNodes.length || j >= allNodes.length) return;
        const node1 = allNodes[i], node2 = allNodes[j];
        const x1 = offsetX + node1.x * scale, y1 = offsetY + node1.y * scale;
        const x2 = offsetX + node2.x * scale, y2 = offsetY + node2.y * scale;
        const pulse = Math.sin(time * 1.5 + i * 0.3) * 0.3 + 0.7;

        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(59, 130, 246, ${0.2 * pulse})`; ctx.lineWidth = 2; ctx.stroke();

        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(147, 197, 253, ${0.4 * pulse})`; ctx.lineWidth = 0.8; ctx.stroke();
      });

      allNodes.forEach((node, i) => {
        const x = offsetX + node.x * scale, y = offsetY + node.y * scale;
        const pulse = Math.sin(time * 2 + i * 0.5) * 0.3 + 0.7;
        if (node.glow) {
          const nodeGlow = ctx.createRadialGradient(x, y, 0, x, y, 8 * pulse);
          nodeGlow.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
          nodeGlow.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)');
          nodeGlow.addColorStop(1, 'transparent');
          ctx.fillStyle = nodeGlow;
          ctx.beginPath(); ctx.arc(x, y, 8 * pulse, 0, Math.PI * 2); ctx.fill();
        }
        ctx.beginPath(); ctx.arc(x, y, 2.5 * pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(219, 234, 254, ${0.9 * pulse})`; ctx.fill();
      });

      const sphereX = offsetX + sphereCenter.x * scale;
      const sphereY = offsetY + sphereCenter.y * scale;
      const spherePulse = Math.sin(time * 2) * 0.1 + 1;

      const sphereGlow = ctx.createRadialGradient(sphereX, sphereY, 0, sphereX, sphereY, 60 * spherePulse);
      sphereGlow.addColorStop(0, 'rgba(200, 200, 200, 0.4)');
      sphereGlow.addColorStop(0.3, 'rgba(255, 255, 255, 0.2)');
      sphereGlow.addColorStop(0.7, 'rgba(150, 150, 150, 0.1)');
      sphereGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = sphereGlow;
      ctx.beginPath(); ctx.arc(sphereX, sphereY, 60 * spherePulse, 0, Math.PI * 2); ctx.fill();

      ctx.beginPath(); ctx.arc(sphereX, sphereY, 15, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(200, 200, 200, 0.3)'; ctx.fill();
      ctx.strokeStyle = 'rgba(220, 220, 220, 0.6)'; ctx.lineWidth = 1.5; ctx.stroke();

      for (let ring = 0; ring < 3; ring++) {
        ctx.beginPath();
        ctx.ellipse(sphereX, sphereY, 25 + ring * 8, (25 + ring * 8) * 0.4, time * 0.5 + ring * 1, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(139, 92, 246, ${0.15 - ring * 0.03})`; ctx.lineWidth = 1; ctx.stroke();
      }

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        const pPulse = Math.sin(time * 2 + p.x * 0.01) * 0.3 + 0.7;
        const pGlow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        pGlow.addColorStop(0, p.color + '60'); pGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = pGlow;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size * pPulse, 0, Math.PI * 2);
        ctx.fillStyle = p.color; ctx.fill();
      });

      for (let i = 0; i < 6; i++) {
        const barWidth = 40 + Math.sin(time * 0.5 + i * 0.7) * 25;
        const alpha = 0.15 + Math.sin(time + i) * 0.05;
        ctx.fillStyle = `rgba(59, 130, 246, ${alpha * 0.5})`;
        ctx.fillRect(25, 100 + i * 20, barWidth + 4, 5);
        ctx.fillStyle = `rgba(147, 197, 253, ${alpha})`;
        ctx.fillRect(25, 101 + i * 20, barWidth, 3);
      }

      ctx.beginPath(); ctx.arc(70, 240, 25, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(70, 240, 18, time * 0.5, time * 0.5 + Math.PI * 1.5);
      ctx.strokeStyle = 'rgba(200, 200, 200, 0.4)'; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); ctx.arc(70, 240, 5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'; ctx.fill();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      for (let x = 0; x < w; x += 30) {
        for (let y = 0; y < h; y += 30) {
          if (Math.random() > 0.7) { ctx.beginPath(); ctx.arc(x, y, 1, 0, Math.PI * 2); ctx.fill(); }
        }
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTH PAGE
// ═══════════════════════════════════════════════════════════════════════════

const AuthPage: React.FC = () => {
  const { login, signup, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!forgotEmail.trim()) {
      setError('Enter your email first');
      return;
    }
    setIsLoading(true);
    const result = await forgotPassword(forgotEmail.trim());
    setIsLoading(false);
    if (result.ok) setForgotSent(true);
    else setError(result.error || 'Could not send reset link.');
  };

  // Autofill / password-manager fields don't always fire the 'input' event
  // React listens to for controlled inputs, so on a fresh fill-then-submit
  // (no manual keystroke) `email`/`password` state can stay stale - refs
  // read the real, current DOM value at submit time as a fallback so the
  // form never silently submits empty or leftover values from before.
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const emailVal = (emailRef.current?.value ?? email).trim();
    const passwordVal = passwordRef.current?.value ?? password;
    const nameVal = (nameRef.current?.value ?? name).trim();

    let result: { ok: boolean; error?: string };

    if (isLogin) {
      if (!emailVal || !passwordVal) {
        setError('Email and password are required');
        setIsLoading(false);
        return;
      }
      result = await login(emailVal, passwordVal);
      if (!result.ok) setError(result.error || 'Invalid email or password');
    } else {
      if (!nameVal || !emailVal || !passwordVal) {
        setError('All fields are required');
        setIsLoading(false);
        return;
      }
      if (passwordVal.length < 8) {
        setError('Password must be at least 8 characters');
        setIsLoading(false);
        return;
      }
      result = await signup(nameVal, emailVal, passwordVal);
      if (!result.ok) setError(result.error || 'Could not create account.');
    }

    setIsLoading(false);
    if (result.ok) navigate(websiteSignInDestination(location.search));
  };

  return (
    <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden">
      <NeuralMeshBackground />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a1a]/90 via-[#0a0a1a]/65 to-transparent" />

      <div className="relative z-10 flex min-h-full">
        {/* Left side - Auth Form */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full lg:w-[440px] flex flex-col justify-center px-10 lg:px-12 py-10"
        >
          {/* Logo — NOW USES SAME MIRAAI LOGO AS REST OF APP */}
          <div className="flex items-center gap-3 mb-10">
            <motion.div
              whileHover={{ scale: 1.05, rotate: 3 }}
              whileTap={{ scale: 0.95 }}
            >
              <MiraalIcon size={40} />
            </motion.div>
            <div>
              <MiraalWordmark size={22} />
              <p className="text-[11px] text-[#bbb] font-medium tracking-widest uppercase mt-1">Agent Swarm Intelligence</p>
            </div>
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <AnimatePresence mode="wait">
              <motion.h2
                key={showForgot ? 'forgot' : isLogin ? 'welcome-back' : 'create-account'}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="text-[28px] font-bold text-white mb-2 tracking-tight leading-tight"
              >
                {showForgot ? 'Reset password' : isLogin ? "Hey, let's build something" : 'Create account'}
              </motion.h2>
            </AnimatePresence>
            <p className="text-sm text-[#ccc]">
              {showForgot
                ? "We'll email you a link to reset it"
                : isLogin ? 'Login to launch your agents' : 'Join the future of AI collaboration'}
            </p>
          </div>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm overflow-hidden"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Forgot-password form */}
          {showForgot ? (
            forgotSent ? (
              <div>
                <p className="text-sm text-[#ccc] mb-4">
                  If an account exists for <span className="text-white">{forgotEmail}</span>, we've sent a reset link to it.
                </p>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotSent(false); setError(null); }}
                  className="text-[13px] text-[#a78bfa] hover:text-[#c4b5fd] font-semibold transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label className="text-[11px] font-medium text-[#999] mb-1.5 block">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ddd]" />
                    <input
                      type="email"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#12121f]/90 border border-[#2a2a40] text-white text-sm placeholder-[#888] focus:outline-none focus:border-[#8b5cf6]/50 focus:ring-1 focus:ring-[#8b5cf6]/10 transition-all"
                    />
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-1"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed, #ec4899)',
                    boxShadow: '0 4px 20px rgba(139, 92, 246, 0.35)',
                  }}
                >
                  {isLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                  ) : 'Send reset link'}
                </motion.button>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setError(null); }}
                  className="text-[13px] text-[#a78bfa] hover:text-[#c4b5fd] font-semibold transition-colors"
                >
                  Back to sign in
                </button>
              </form>
            )
          ) : (
          <>
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <AnimatePresence>
              {!isLogin && (
                <motion.div
                  key="name-field"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <label className="text-[11px] font-medium text-[#999] mb-1.5 block">Full Name</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ddd]" />
                    <input
                      ref={nameRef}
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#12121f]/90 border border-[#2a2a40] text-white text-sm placeholder-[#888] focus:outline-none focus:border-[#8b5cf6]/50 focus:ring-1 focus:ring-[#8b5cf6]/10 transition-all"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="text-[11px] font-medium text-[#999] mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ddd]" />
                <input
                  ref={emailRef}
                  type="email" autoComplete={isLogin ? 'username' : 'email'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#12121f]/90 border border-[#2a2a40] text-white text-sm placeholder-[#888] focus:outline-none focus:border-[#8b5cf6]/50 focus:ring-1 focus:ring-[#8b5cf6]/10 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-[#999] mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ddd]" />
                <input
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 rounded-xl bg-[#12121f]/90 border border-[#2a2a40] text-white text-sm placeholder-[#888] focus:outline-none focus:border-[#8b5cf6]/50 focus:ring-1 focus:ring-[#8b5cf6]/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#ddd] hover:text-[#999] transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {isLogin && (
                <div className="flex justify-end mt-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setForgotEmail(email); setError(null); }}
                    className="text-[11px] text-[#a78bfa] hover:text-[#c4b5fd] transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-1"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed, #ec4899)',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.35)',
              }}
            >
              {isLoading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={16} />
                </>
              )}
            </motion.button>
          </form>

          {/* Google Sign-In */}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[11px] text-[#888]">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button
            type="button"
            onClick={() => { window.location.href = `${API_BASE_URL}/api/v1/auth/google`; }}
            className="w-full mt-4 py-3 rounded-xl text-white font-medium text-sm transition-all flex items-center justify-center gap-2 border border-white/15 hover:bg-white/5"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#bbb]">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                onClick={() => { setIsLogin(!isLogin); setError(null); }}
                className="ml-1.5 text-[#a78bfa] hover:text-[#c4b5fd] font-semibold transition-colors"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
          </>
          )}

          {/* Features */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 flex items-center gap-4 text-[11px] text-[#ccc] font-medium"
          >
            <div className="flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#8b5cf6]" />
              <span>Agent Swarm</span>
            </div>
            <div className="w-px h-3 bg-[#222]" />
            <div className="flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#4ECDC4]" />
              <span>Made in India</span>
            </div>
            <div className="w-px h-3 bg-[#222]" />
            <div className="flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#ec4899]" />
              <span>Build Anything</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Right side - Big AI text */}
        <div className="hidden lg:flex flex-1 items-center justify-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-center"
          >
            <div className="text-[150px] font-bold text-transparent bg-clip-text opacity-25 leading-none"
              style={{ backgroundImage: 'linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)' }}>
              AI
            </div>
            <p className="text-sm text-[#ccc] mt-4 tracking-[0.25em] uppercase font-medium">Agent Swarm. One Mind.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
